export interface Resource {
  id: string;
  slug: string;
  title: string;
  category: string;
  categoryColor: string;
  excerpt: string;
  icon: string;
  tags: string[];
  body: ResourceSection[];
  relatedSlugs?: string[];
}

export interface ResourceSection {
  heading?: string;
  content: string;
  list?: string[];
}

export const mockResources: Resource[] = [
  {
    id: 'res-1',
    slug: 'welcome-to-panorama',
    title: 'Welcome to Panorama',
    category: 'Getting Started',
    categoryColor: '#4CC9FF',
    excerpt:
      'Everything you need to know as a new resident of Panorama Towers — the community, the platform, and how to get connected.',
    icon: '🏙️',
    tags: ['New Residents', 'Getting Started', 'Overview'],
    relatedSlugs: ['how-verification-works', 'community-guidelines'],
    body: [
      {
        heading: 'Welcome to the Community',
        content:
          'Panorama Towers is home to a diverse and engaged community of owners, renters, and residents across both towers. This platform is your private, resident-first space to connect, discover services, browse the marketplace, and stay informed.',
      },
      {
        heading: 'What You Can Do Here',
        content: 'As a verified resident, you have access to:',
        list: [
          'The Resident Directory — find and connect with neighbors',
          'Services — discover and offer services within the community',
          'Marketplace — buy and sell with fellow residents',
          'Conversations — join discussions, ask questions, share recommendations',
          'Events — stay up to date on community events',
          'Newsletter — access the private community bulletin',
        ],
      },
      {
        heading: 'Getting Verified',
        content:
          'To access the full platform, you\'ll need to complete verification. This involves confirming your residency and, optionally, being vouched for by an existing verified resident. See "How Verification Works" for full details.',
      },
    ],
  },
  {
    id: 'res-2',
    slug: 'how-verification-works',
    title: 'How Verification Works',
    category: 'Trust & Safety',
    categoryColor: '#2DD4A0',
    excerpt:
      'A plain-English explanation of how we verify residents, why it matters, and what to expect during the process.',
    icon: '✅',
    tags: ['Verification', 'Trust', 'Onboarding'],
    relatedSlugs: ['welcome-to-panorama', 'privacy-and-trust'],
    body: [
      {
        heading: 'Why Verification Matters',
        content:
          'This platform is private and resident-only. Verification ensures that everyone you interact with is a genuine Panorama Towers resident. It protects the community and maintains the trust that makes this network valuable.',
      },
      {
        heading: 'The Verification Process',
        content: 'Verification involves three steps:',
        list: [
          'Submit a request with your name, tower, unit, and contact details',
          'Our team reviews your submission against building records',
          'Optionally, an existing verified resident can vouch for you to speed up the process',
        ],
      },
      {
        heading: 'Vouching',
        content:
          'Vouching is an optional but valued part of the process. If a verified resident vouches for you, your verification is typically approved faster. Vouches are visible on your profile as a trust signal to other residents.',
      },
      {
        heading: 'How Long Does It Take?',
        content:
          'Most verifications are completed within 24–48 hours. If you\'ve been vouched for by an existing resident, it\'s often faster. You\'ll receive a notification once your verification is approved.',
      },
    ],
  },
  {
    id: 'res-3',
    slug: 'community-guidelines',
    title: 'Community Guidelines',
    category: 'Trust & Safety',
    categoryColor: '#2DD4A0',
    excerpt:
      'The principles and rules that keep Panorama\'s community network respectful, useful, and trustworthy for all residents.',
    icon: '📋',
    tags: ['Guidelines', 'Rules', 'Community'],
    relatedSlugs: ['privacy-and-trust', 'why-this-platform-is-unofficial'],
    body: [
      {
        heading: 'Our Community Principles',
        content:
          'Panorama Towers is a private community. The standards here reflect the kind of neighbor-to-neighbor respect we expect in the building itself.',
      },
      {
        heading: 'What We Expect',
        content: '',
        list: [
          'Be respectful in all interactions — this is your neighbors\' community too',
          'Keep listings and services accurate and honest',
          'Do not share other residents\' contact information without consent',
          'Do not post content that is discriminatory, harassing, or threatening',
          'Reviews must be genuine and based on real experiences',
          'Do not use this platform for commercial solicitation outside the services directory',
        ],
      },
      {
        heading: 'Moderation',
        content:
          'Content that violates these guidelines may be removed. Repeated violations may result in account suspension. Reports are reviewed by the platform team, not building management.',
      },
      {
        heading: 'Reporting',
        content:
          'If you see content that violates these guidelines, use the report function on any post, listing, or profile. Reports are confidential.',
      },
    ],
  },
  {
    id: 'res-4',
    slug: 'facebook-group-info',
    title: 'Facebook Group Info',
    category: 'Community Channels',
    categoryColor: '#9B7FFF',
    excerpt:
      'Details about the unofficial Panorama Towers Facebook group, how it relates to this platform, and how to join.',
    icon: '👥',
    tags: ['Facebook', 'Community', 'Social Media'],
    relatedSlugs: ['whatsapp-privacy-guidance', 'why-this-platform-is-unofficial'],
    body: [
      {
        heading: 'The Facebook Group',
        content:
          'There is an unofficial Panorama Towers Facebook group that has been active for several years. It\'s a useful space for quick questions and general community chatter, though it lacks the structure and privacy controls of this platform.',
      },
      {
        heading: 'How to Join',
        content:
          'The Facebook group is managed independently by a resident volunteer. To join, search for "Panorama Towers Residents" on Facebook and request to join. Approval is typically granted within a day or two.',
      },
      {
        heading: 'Facebook vs. This Platform',
        content:
          'The Facebook group and this platform serve different purposes. Facebook is good for casual, real-time conversation. This platform is designed for structured community interaction with stronger privacy controls, verified identities, and a permanent searchable record.',
      },
    ],
  },
  {
    id: 'res-5',
    slug: 'whatsapp-privacy-guidance',
    title: 'WhatsApp Privacy Guidance',
    category: 'Community Channels',
    categoryColor: '#9B7FFF',
    excerpt:
      'How to use the building WhatsApp groups safely, what to share and what not to share, and how your privacy is protected here.',
    icon: '💬',
    tags: ['WhatsApp', 'Privacy', 'Community'],
    relatedSlugs: ['facebook-group-info', 'privacy-and-trust'],
    body: [
      {
        heading: 'Building WhatsApp Groups',
        content:
          'There are several WhatsApp groups associated with Panorama Towers — some floor-specific, some tower-wide. These are informal and not managed by this platform.',
      },
      {
        heading: 'Privacy Considerations',
        content: 'When using WhatsApp groups, keep in mind:',
        list: [
          'Your phone number is visible to all group members',
          'Messages can be screenshotted and shared outside the group',
          'Group membership is not verified — not everyone may be a current resident',
          'Avoid sharing sensitive personal information in group chats',
        ],
      },
      {
        heading: 'This Platform vs. WhatsApp',
        content:
          'On this platform, your phone number is hidden by default. You control what contact information is visible. Identities are verified. This makes it a safer space for community interaction that involves personal details.',
      },
    ],
  },
  {
    id: 'res-6',
    slug: 'why-this-platform-is-unofficial',
    title: 'Why This Platform Is Unofficial',
    category: 'About This Platform',
    categoryColor: '#F0A832',
    excerpt:
      'A transparent explanation of what "unofficial" means, why this platform exists independently of building management, and why that matters.',
    icon: '🏛️',
    tags: ['Unofficial', 'Independence', 'Transparency'],
    relatedSlugs: ['privacy-and-trust', 'community-guidelines'],
    body: [
      {
        heading: 'What "Unofficial" Means',
        content:
          'This platform is not affiliated with, endorsed by, or operated by Panorama Towers building management or the HOA. It is a resident-created and resident-run community network.',
      },
      {
        heading: 'Why Independence Matters',
        content:
          'An independent platform means your conversations, reviews, and community interactions are not visible to or controlled by building management. Residents can speak freely, share honest service reviews, and organize without institutional oversight.',
      },
      {
        heading: 'What This Platform Is Not',
        content: '',
        list: [
          'Not a management communication channel',
          'Not an HOA tool',
          'Not affiliated with any property management company',
          'Not a replacement for official building communications',
        ],
      },
      {
        heading: 'Our Commitment',
        content:
          'We are committed to keeping this platform independent, private, and resident-first. We do not share data with building management. We do not accept management oversight of content moderation.',
      },
    ],
  },
  {
    id: 'res-7',
    slug: 'privacy-and-trust',
    title: 'Privacy & Trust',
    category: 'Trust & Safety',
    categoryColor: '#2DD4A0',
    excerpt:
      'How this platform protects your privacy, what data we collect, and the principles behind our approach to resident trust.',
    icon: '🔒',
    tags: ['Privacy', 'Data', 'Trust'],
    relatedSlugs: ['how-verification-works', 'community-guidelines'],
    body: [
      {
        heading: 'Your Privacy by Default',
        content:
          'When you join this platform, your phone number and email address are hidden from other residents by default. You can choose to make them visible in your privacy settings, but we start from a position of privacy, not exposure.',
      },
      {
        heading: 'What Other Residents Can See',
        content: 'By default, other verified residents can see:',
        list: [
          'Your name and profile photo (if uploaded)',
          'Your tower and unit number',
          'Your resident type (Owner, Renter, Resident)',
          'Your bio, profession, and services (if you\'ve added them)',
          'Your interests and social links (if you\'ve added them)',
        ],
      },
      {
        heading: 'What Is Always Hidden',
        content: '',
        list: [
          'Your phone number (unless you choose to show it)',
          'Your email address (unless you choose to show it)',
          'Your verification submission details',
        ],
      },
      {
        heading: 'Data We Collect',
        content:
          'We collect only what is necessary to operate the platform: your name, contact details for verification, and the content you choose to post. We do not sell your data. We do not share it with building management or third parties.',
      },
    ],
  },
  {
    id: 'res-8',
    slug: 'faq',
    title: 'Frequently Asked Questions',
    category: 'Getting Started',
    categoryColor: '#4CC9FF',
    excerpt:
      'Answers to the most common questions from new and existing residents about the platform, verification, and community features.',
    icon: '❓',
    tags: ['FAQ', 'Help', 'Getting Started'],
    relatedSlugs: ['welcome-to-panorama', 'how-verification-works'],
    body: [
      {
        heading: 'Who can join this platform?',
        content:
          'Any current resident of Panorama Towers — owners, renters, and residents — can request access. Verification is required before full access is granted.',
      },
      {
        heading: 'Is this platform free?',
        content:
          'Yes. The platform is free for all verified residents. There are no subscription fees or paywalls for community features.',
      },
      {
        heading: 'Can building management see my posts?',
        content:
          'No. This platform is independent of building management. Your posts, conversations, and profile information are not visible to or accessible by building management or the HOA.',
      },
      {
        heading: 'How do I report a problem?',
        content:
          'Use the report function on any post, listing, profile, or service. Reports are reviewed by the platform moderation team, not building management.',
      },
      {
        heading: 'Can I delete my account?',
        content:
          'Yes. You can request account deletion from your account settings. All your data will be removed from the platform.',
      },
    ],
  },
];

export function getResourceBySlug(slug: string): Resource | undefined {
  return mockResources.find((r) => r.slug === slug);
}

export const resourceCategories = [
  'All',
  'Getting Started',
  'Trust & Safety',
  'Community Channels',
  'About This Platform',
];
